import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
} from '@mui/material'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CreateActionsPayload, useCreateActions } from '../hooks/useCreateActions'
import { usePatchModels } from '../hooks/usePatchModels'
import { ModelListRowModel } from '../types'
import { useDeleteActions } from '../hooks/useDeleteActions'

const multiplierSchema = z.object({
  multiplier: z
    .number({ required_error: 'Multiplier is required' })
    .min(0.01, 'Multiplier must be greater than 0')
    .max(100, 'Multiplier must be less than 100'),
})

type MultiplierFormData = z.infer<typeof multiplierSchema>

interface UpdatePricesProps {
  selectedRows: ModelListRowModel[]
  disabled?: boolean
  className?: string
}

const steps = ['Enter Multiplier', 'Preview Changes']

export const UpdatePrices = ({ selectedRows, disabled, className }: UpdatePricesProps) => {
  const [open, setOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [multiplier, setMultiplier] = useState<number | null>(null)
  const { mutate: createAction, isPending: isCreatingAction } = useCreateActions()
  const { mutate: deleteActions, isPending: isDeletingActions } = useDeleteActions()
  const { mutate: patchModels, isPending: isPatchingModels } = usePatchModels()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MultiplierFormData>({
    resolver: zodResolver(multiplierSchema),
  })

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setActiveStep(0)
    setMultiplier(null)
    reset()
  }

  const handleNext = () => {
    handleSubmit((data) => {
      setMultiplier(data.multiplier)
      setActiveStep((prevStep) => prevStep + 1)
    })()
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const computeNewPrice = (price: number, mult: number) => Math.round(price * mult)

  const onSubmit = () => {
    if (multiplier === null) return

    const updatedActions: CreateActionsPayload[] = selectedRows.map((model) => ({
      model: model.stock_code,
      new_value: computeNewPrice(model.price, multiplier),
      old_value: model.price,
      type: 'price_change',
    }))

    // Pre-calculate model updates based on the new prices
    const modelUpdates = selectedRows.map((model) => ({
      id: model.id,
      price: computeNewPrice(model.price, multiplier),
    }))

    createAction(updatedActions, {
      onSuccess: (response) => {
        // Update models_v2 using usePatchModels hook
        patchModels(modelUpdates, {
          onSuccess: () => {
            handleClose()
          },
          onError: () => {
            // rollback if patchModels fails
            deleteActions(response.flatMap((action) => (action ? [action.id] : [])))
          },
        })
      },
    })
  }

  const isLoading = isCreatingAction || isPatchingModels || isDeletingActions
  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={disabled || selectedRows.length === 0}
        endIcon={isLoading ? <CircularProgress size={16} /> : null}
        size="small"
        className={className}
      >
        Update Prices
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Update Prices</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {activeStep === 0 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                {...register('multiplier', { valueAsNumber: true })}
                label="Price Multiplier"
                type="number"
                fullWidth
                margin="normal"
                error={!!errors.multiplier}
                helperText={
                  errors.multiplier?.message ||
                  'Enter a multiplier (e.g., 0.8 for 20% discount, 1.2 for 20% increase)'
                }
                inputProps={{
                  step: 0.01,
                  min: 0.01,
                  max: 100,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will update prices for {selectedRows.length} selected models.
              </Typography>
            </Box>
          )}

          {activeStep === 1 && multiplier !== null && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Review the price changes below. The multiplier of <strong>{multiplier}</strong>{' '}
                  will be applied to {selectedRows.length} models.
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 400, overflow: 'auto' }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Stock Code</TableCell>
                        <TableCell align="right">Current Price</TableCell>
                        <TableCell align="center">→</TableCell>
                        <TableCell align="right">New Price</TableCell>
                        <TableCell align="right">Change</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRows.map((model) => {
                        const newPrice = computeNewPrice(model.price, multiplier)
                        const change = newPrice - model.price
                        const changePercent = ((change / model.price) * 100).toFixed(1)
                        return (
                          <TableRow key={model.id}>
                            <TableCell>{model.stock_code}</TableCell>
                            <TableCell align="right">{model.price} CZK</TableCell>
                            <TableCell align="center">→</TableCell>
                            <TableCell align="right">
                              <strong>{newPrice} CZK</strong>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  change > 0
                                    ? 'success.main'
                                    : change < 0
                                      ? 'error.main'
                                      : 'text.primary',
                              }}
                            >
                              {change > 0 ? '+' : ''}
                              {change} CZK ({change > 0 ? '+' : ''}
                              {changePercent}%)
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isLoading}>
              Back
            </Button>
          )}
          {activeStep === 0 ? (
            <Button variant="contained" onClick={handleNext} disabled={isLoading}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
            >
              {isLoading ? 'Updating...' : 'Confirm & Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
